import { cva, VariantProps } from 'class-variance-authority'
import { isNil } from 'lodash-es'
import { ComponentProps } from 'react'

import { cn, useFixWidth } from '@/shared/lib'
import { Chip } from '@/shared/ui'

const DIALOG_MEDIUM_OFFSET = 17.5
const DEFAULT_MEDIUM_OFFSET = 11.5
const LARGE_OFFSET = 15

const filterChipVariants = cva(
  [
    'typo-label1-reading shrink-0 font-medium text-black',
    '[&_svg]:size-auto [&_svg]:text-gray-300 [&_svg]:transition-colors hover:[&_svg]:text-black',
    'data-[selected=true]:border-purple-500 [&[data-selected=true]_svg]:text-black',
  ],

  {
    variants: {
      type: {
        default: '',
        dialog: '',
      },
      size: {
        medium: '',
        large: 'px-2.5 pt-3.5 pb-1.5',
      },
    },

    compoundVariants: [
      {
        type: 'default',
        size: 'medium',
        className: 'h-10 px-2.5 py-[5px]',
      },
      {
        type: 'default',
        size: 'large',
        className: 'h-25.5',
      },
      {
        type: 'dialog',
        size: 'medium',
        className: 'h-11 px-4 py-2.5',
      },
      {
        type: 'dialog',
        size: 'large',
        className: 'h-27',
      },
    ],
  },
)

type FilterChipVariant = VariantProps<typeof filterChipVariants>

type Props = Omit<ComponentProps<typeof Chip>, 'variant' | 'shape' | 'size'> &
  FilterChipVariant & {
    initialWidth?: number
  }

export const FilterChip = ({
  type = 'default',
  size = 'medium',
  children,
  className,
  startIcon,
  initialWidth,
  ...restProps
}: Props) => {
  const offset = (() => {
    if (size === 'large') {
      return LARGE_OFFSET
    }

    if (type === 'default') {
      return DEFAULT_MEDIUM_OFFSET
    }

    // startIcon이 있는 경우 좌측 여백이 4px 줄어듦
    return isNil(startIcon) ? DIALOG_MEDIUM_OFFSET : DIALOG_MEDIUM_OFFSET - 2
  })()

  const { width: widthFromChildren, childrenRef } = useFixWidth<HTMLDivElement>(
    children,
    { offset },
  )

  return (
    <Chip
      variant="outline"
      shape="rounded"
      width={widthFromChildren ?? initialWidth}
      childrenRef={childrenRef}
      className={cn(filterChipVariants({ type, size }), className)}
      startIcon={startIcon}
      {...restProps}
    >
      {children}
    </Chip>
  )
}
